/**
 * Aides de l'onglet « Recette et bascule » (ARTO-134). Rien ici ne décide si
 * une recette est bonne : l'API rend les compteurs, l'écran les affiche. Seul
 * le pied du tableau est calculé, par la même règle que l'API (§ 8.11).
 */
import { cleOuverture } from './ouvertures.js';
import type { ControleRecette, EtapeBascule, LigneRecette, RecetteOuverture } from '../types.js';

export const cleRecette = (id: string): readonly unknown[] => [...cleOuverture(id), 'recette'];

export const CONTROLES: readonly ControleRecette[] = [
  'historique_localisations',
  'echantillon_fiches',
  'images_principales',
  'factures_anterieures',
];

/** Les étapes de la bascule, dans l'ordre où l'API les fait. */
export const ETAPES_BASCULE: readonly EtapeBascule[] = [
  'controle',
  'scellement',
  'mandat',
  'purge',
  'invitations',
  'mise_en_service',
];

/** Ce qui reste à expliquer d'une nature : total − (créés + complétés + ignorés + refusés + écartés). */
export function ecartDe(
  ligne: Pick<LigneRecette, 'total' | 'crees' | 'completes' | 'ignores' | 'refuses' | 'ecartes'>,
): number {
  return (
    ligne.total - (ligne.crees + ligne.completes + ligne.ignores + ligne.refuses + ligne.ecartes)
  );
}

export type TotauxRecette = Omit<LigneRecette, 'nature' | 'motifs'>;

/** La ligne de pied : chaque colonne est la somme des natures, l'écart suit la même règle. */
export function totauxRecette(lignes: readonly LigneRecette[]): TotauxRecette {
  const somme = (champ: Exclude<keyof TotauxRecette, 'ecart'>): number =>
    lignes.reduce((total, ligne) => total + ligne[champ], 0);
  const cumul = {
    lots: somme('lots'),
    total: somme('total'),
    crees: somme('crees'),
    completes: somme('completes'),
    ignores: somme('ignores'),
    refuses: somme('refuses'),
    ecartes: somme('ecartes'),
    aRevoir: somme('aRevoir'),
  };
  return { ...cumul, ecart: ecartDe(cumul) };
}

export type StatutEtapeBascule = 'faite' | 'erreur' | 'en_cours' | 'attente';

/**
 * Où en est une étape : faite si l'API l'a marquée, en erreur si c'est celle
 * où la dernière tentative s'est arrêtée, en cours pour la première non faite
 * tant que le bail court.
 */
export function statutEtape(
  bascule: RecetteOuverture['bascule'],
  cle: EtapeBascule,
): StatutEtapeBascule {
  const faite = (candidate: EtapeBascule): boolean =>
    bascule.etapes.some((etape) => etape.cle === candidate);
  if (faite(cle)) return 'faite';
  if (bascule.erreur?.etape === cle) return 'erreur';
  const premiere = ETAPES_BASCULE.find((candidate) => !faite(candidate));
  return bascule.enCours && premiere === cle ? 'en_cours' : 'attente';
}
